import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { SettingsSelectors } from '@app/main/settings/store';
import { ArrayUtils } from '@app/shared/utils/array-utils';
import * as fromRoot from '@app/store/reducers';
import { SettingValue, Unit } from '@cco/model';
import * as UnitSelectors from '@core/units/store/unit.selectors';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, startWith, tap } from 'rxjs/operators';
import { ENTITY_SERVICE, EntityService } from '@core/services/entity.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { FormGroup } from '@ngneat/reactive-forms';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption } from '@angular/material/core';

import { MatError, MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';

@UntilDestroy()
@Component({
    selector: 'app-unit-select',
    templateUrl: './unit-select.component.html',
    styleUrls: ['./unit-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UnitSelectComponent),
            multi: true,
        },
    ],
    imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    MatOption,
    MatTooltip,
    MatInput,
    MatPrefix,
    MatError
]
})
export class UnitSelectComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  protected store = inject(Store);
  protected entityService = inject<EntityService>(ENTITY_SERVICE);
  private injector = inject(Injector);

  units: Unit[];
  selectedUnit: Unit;

  @Input()
  set prioritizeUnitIds(unitIds: number[]) {
    this._prioritizedUnitIds$.next(unitIds);
  }
  _prioritizedUnitIds$ = new BehaviorSubject<number[] | null>(null);

  @Input() validUnitIds: number[];
  validUnitIds$ = new BehaviorSubject<number[]>(null);

  @Input()
  set dimensionIds(dimensionIds: number[]) {
    this._dimensionIds$.next(dimensionIds);
  }
  _dimensionIds$ = new BehaviorSubject<number[] | null>(null);

  @Input() label = 'Unit';
  @Input() required = false;
  @Input() readonly = false;
  @Input() leftPadding = false;
  @Input() width = '100px';

  /**
   * The order of prescedence for setting the default unit is:
   *  1. defaultUnitId
   *  2. defaultSettingId
   *  3. defaultSettingPath
   */

  resolvedDefaultUnitId: number;
  @Input() defaultUnitId: number;
  @Input() defaultSettingId: number;
  defaultSettingId$ = new BehaviorSubject<number>(null);
  @Input() defaultSettingPath: string[];
  defaultSettingPath$ = new BehaviorSubject<string[]>(null);

  @Output()
  selectionChange = new EventEmitter<MatSelectChange>();

  unitIdFromParentForm: number;

  formControl: UntypedFormControl;

  private parentIsDirty = false;

  onChange: (unitId: number) => void;
  onTouched: () => void;

  constructor() {
    this.formControl = new UntypedFormControl();

    this.formControl.valueChanges
      .pipe(
        untilDestroyed(this),
        map(value => (value === -1 ? null : value)),
      )
      .subscribe((value) => {
        if (this.formControl.enabled && this.formControl.dirty && this.onChange) {
          this.onChange(value);
        }
        this.selectedUnit = this.units?.find(unit => unit?.id === value);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultUnitId) {
      if (this.defaultUnitId) {
        this.resolvedDefaultUnitId = this.defaultUnitId;
        this.refreshDefault();
      }
    }
    if (changes.defaultSettingId) {
      this.defaultSettingId$.next(this.defaultSettingId);
    }
    if (changes.defaultSettingPath) {
      this.defaultSettingPath$.next(this.defaultSettingPath);
    }
    if (changes.required) {
      if (this.required) {
        this.formControl.setValidators(Validators.required);
      } else {
        this.formControl.clearValidators();
      }
    }
    if (changes.validUnitIds) {
      this.validUnitIds$.next(this.validUnitIds);
    }
    if (changes.dimensionIds) {
      this._dimensionIds$.next(changes.dimensionIds.currentValue);
    }
  }

  ngOnInit() {
    this.wireParentDirtyDetectionToPushUpDefaultValue();

    combineLatest([this.validUnitIds$, this._dimensionIds$, this._prioritizedUnitIds$])
      .pipe(
        distinctUntilChanged(),
        tap(() => this.formControl.markAsUntouched()),
        mergeMap(([validUnitsIds, dimensionIds, prioritizedUnitsIds]) => {
          if (!validUnitsIds) {
            if (dimensionIds) {
              return this.store.select(UnitSelectors.selectUnitsByDimensionIds, { dimensionIds }).pipe(
                map<Unit[], [Unit[], number[]]>(units => [units, prioritizedUnitsIds]),
              );
            }
            return this.store.select(fromRoot.unitSelectors.selectEntities).pipe(
              map<Unit[], [Unit[], number[]]>(units => [units, prioritizedUnitsIds]),
            );
          }

          return this.store.select(UnitSelectors.selectUnitsByIds({ ids: validUnitsIds })).pipe(
            map<Unit[], [Unit[], number[]]>(units => [units, prioritizedUnitsIds]),
          );
        }),
        distinctUntilChanged((a, b) => this.compareUnitArrayAndPrioritizedIds(a, b)),
        map(([units, prioritizedUnitIds]) => {
          if (prioritizedUnitIds == null) {
            return units;
          }

          return units.sort((a, b) => {
            if (prioritizedUnitIds.includes(a.id) && !prioritizedUnitIds.includes(b.id)) return -1;
            if (!prioritizedUnitIds.includes(a.id) && prioritizedUnitIds.includes(b.id)) return 1;
            return 0;
          });
        }),
        tap(() => this.refreshDefault()),
        untilDestroyed(this),
      )
      .subscribe(units => (this.units = units));

    combineLatest([
      this.defaultSettingId$.pipe(
        filter(id => !!id),
        distinctUntilChanged(),
        mergeMap(settingId =>
          this.store.select(SettingsSelectors.selectResolvedSettingBySettingId(settingId)),
        ),
        startWith(null),
      ),
      this.defaultSettingPath$.pipe(
        filter(path => !!path),
        distinctUntilChanged(),
        mergeMap(path =>
          this.store.select(SettingsSelectors.selectResolvedSettingByPath(path)),
        ),
        startWith(null),
      ),
    ])
      .pipe(
        map(([settingValueFromId, settingValueFromPath]) => {
          if (settingValueFromId) {
            return settingValueFromId;
          }

          return settingValueFromPath;
        }),
        untilDestroyed(this),
        filter(settingValue => !!settingValue),
        tap((settingValue: SettingValue) => {
          if (!this.defaultUnitId) {
            this.resolvedDefaultUnitId = settingValue.value as number;
            this.refreshDefault();
          }
        }),
      )
      .subscribe();

    this.entityService.loadIfOlderThan('Unit', 'P1D');
  }

  /**
   * This detects when a parent @ngneat/reactive-forms FormGroup becomes
   * dirty, so we can push the default value up to the form.
   */
  private wireParentDirtyDetectionToPushUpDefaultValue() {
    const ngControl = this.injector.get(NgControl);

    let formControl: AbstractControl;

    if (ngControl instanceof FormControlName) {
      formControl = this.injector.get(FormGroupDirective).getControl(ngControl);
    } else {
      formControl = (ngControl as FormControlDirective).form as FormControl;
    }
    if (formControl?.parent instanceof FormGroup) {
      (formControl.parent as unknown as FormGroup<any>).dirty$.pipe(
        untilDestroyed(this),
      ).subscribe((parentIsDirty) => {
        this.parentIsDirty = parentIsDirty;
        if (parentIsDirty)
          this.refreshDefault();
      });
    }
  }

  private compareUnitArrayAndPrioritizedIds(a: [Unit[], number[]], b: [Unit[], number[]]): boolean {
    return (
      ArrayUtils.compareArraysByMemberId(a[0], b[0]) && ArrayUtils.arraysAreEqual(a[1], b[1])
    );
  }

  writeValue(unitId: number) {
    this.unitIdFromParentForm = unitId;
    this.formControl.reset((unitId && unitId) || this.resolvedDefaultUnitId);
  }

  registerOnChange(fn: (unitId: number) => void) {
    this.onChange = fn;
    this.refreshDefault();
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean) {
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
      this.refreshDefault();
    }
  }

  refreshDefault() {
    if (
      this.formControl.untouched &&
      this.formControl.enabled &&
      this.resolvedDefaultUnitId &&
      this.onChange &&
      !this.unitIdFromParentForm
    ) {
      this.formControl.reset(this.resolvedDefaultUnitId, { emitEvent: true });
      if (this.parentIsDirty)
        this.onChange(this.resolvedDefaultUnitId);
    }
  }
}
